// Lepené dielce — pravidlá delenia hrúbky (z NastaveniaPage, KOLO 170).
import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateSplitRule,
  useDeleteSplitRule,
  useSplitRules,
  useUpdateSplitRule,
} from "@/api/hooks/useSplitRules";
import type { SplitRule } from "@/api/schemas";

// ─── Split rules — pravidlá delenia hrúbky lepených dielcov ──

/** Sekcia "Lepené dielce — pravidlá". CRUD nad /api/split-rules. */
export function SplitRulesSection(): JSX.Element {
  const rulesQuery = useSplitRules();
  const deleteMutation = useDeleteSplitRule();
  const [editingRule, setEditingRule] = useState<SplitRule | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const rules = rulesQuery.data?.data ?? [];

  async function handleDelete(rule: SplitRule): Promise<void> {
    if (!window.confirm(`Naozaj zmazať pravidlo pre ${rule.hrubka_mm} mm?`)) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(rule.id);
      toast({ title: "Pravidlo zmazané" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl">Lepené dielce — pravidlá delenia hrúbky</h2>
          <p className="text-xs text-muted-foreground">
            Pri zadaní neštandardnej hrúbky (napr. 36 mm) sa pri vytváraní
            dielca opýta na spôsob výroby — lepený (X+Y) alebo masívny.
            Štandardné hrúbky 10/12/16/18/19/25 sa nepýtajú.
          </p>
          <p className="text-xs text-muted-foreground">
            Vrstvy sú celé mm — doska 18,7 patrí do 18, takže netreba
            pravidlo na každú desatinu. Lepený dielec má skutočný súčet
            vrstiev (z 18,7 + 18,7 je 37,4).
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" /> Pridať
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hrúbka</TableHead>
              <TableHead>Default kombinácia</TableHead>
              <TableHead>Alternatívy</TableHead>
              <TableHead className="w-24 text-right">Akcie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                  Žiadne pravidlá. Default v Tandeme: 28/35/36/38/50/60 mm.
                </TableCell>
              </TableRow>
            ) : (
              rules.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono">{r.hrubka_mm} mm</TableCell>
                  <TableCell className="font-mono">
                    {r.kombinacie[0]?.join(" + ") ?? "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {r.kombinacie.slice(1).map((c) => c.join(" + ")).join(", ") || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingRule(r)}
                      aria-label="Upraviť"
                      title="Upraviť pravidlo delenia hrúbky"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleDelete(r)}
                      aria-label="Zmazať"
                      title="Zmazať pravidlo delenia"
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <SplitRuleDialog
        open={createOpen || editingRule !== null}
        rule={editingRule}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditingRule(null);
          }
        }}
      />
    </section>
  );
}

/** Dialog na pridanie/úpravu pravidla delenia hrúbky. */
function SplitRuleDialog({
  open,
  rule,
  onOpenChange,
}: {
  open: boolean;
  rule: SplitRule | null;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const isEdit = !!rule;
  const createMutation = useCreateSplitRule();
  const updateMutation = useUpdateSplitRule();

  const [hrubkaInput, setHrubkaInput] = useState("");
  // Kombinácie ako CSV pre user-friendly editáciu: "18+18, 12+24"
  const [kombinacieInput, setKombinacieInput] = useState("");

  useEffect(() => {
    if (open) {
      setHrubkaInput(rule ? String(rule.hrubka_mm) : "");
      setKombinacieInput(
        rule
          ? rule.kombinacie.map((c) => c.join("+")).join(", ")
          : "",
      );
    }
  }, [open, rule]);

  function parseKombinacie(input: string): number[][] | null {
    const combos = input
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((combo) =>
        combo
          .split("+")
          .map((n) => parseInt(n.trim(), 10))
          .filter((n) => Number.isFinite(n) && n > 0),
      )
      .filter((c) => c.length > 0);
    return combos.length > 0 ? combos : null;
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const hrubka = parseInt(hrubkaInput, 10);
    if (!Number.isFinite(hrubka) || hrubka <= 0) {
      toast({
        variant: "destructive",
        title: "Neplatná hrúbka",
        description: "Zadaj kladné celé číslo (mm).",
      });
      return;
    }
    const kombinacie = parseKombinacie(kombinacieInput);
    if (!kombinacie) {
      toast({
        variant: "destructive",
        title: "Neplatné kombinácie",
        description: "Použi formát '18+18, 12+24'.",
      });
      return;
    }
    try {
      if (isEdit && rule) {
        await updateMutation.mutateAsync({
          id: rule.id,
          data: { kombinacie },
        });
        toast({ title: "Pravidlo uložené" });
      } else {
        await createMutation.mutateAsync({ hrubka_mm: hrubka, kombinacie });
        toast({ title: "Pravidlo pridané" });
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `Upraviť pravidlo ${rule?.hrubka_mm} mm`
              : "Pridať pravidlo delenia hrúbky"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pravidlo, na aké hrúbky sa dielec rozdelí pri lepení.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sr-hrubka">Hrúbka dielca (mm)</Label>
            <Input
              id="sr-hrubka"
              type="number"
              min={1}
              value={hrubkaInput}
              onChange={(e) => setHrubkaInput(e.target.value)}
              placeholder="36"
              disabled={isEdit}
              autoFocus={!isEdit}
            />
            {isEdit ? (
              <p className="text-[11px] text-muted-foreground">
                Hrúbku nemôžeš zmeniť — vytvor nové pravidlo.
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sr-kombinacie">Kombinácie vrstiev</Label>
            <Input
              id="sr-kombinacie"
              value={kombinacieInput}
              onChange={(e) => setKombinacieInput(e.target.value)}
              placeholder="18+18"
              autoFocus={isEdit}
            />
            <p className="text-[11px] text-muted-foreground">
              Prvá je default. Oddeľ čiarkami. Vrstvy znamienkom +.
              Napr. <span className="font-mono">18+18</span> pre 36 mm.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Zrušiť
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Ukladá sa…"
                : isEdit
                  ? "Uložiť"
                  : "Pridať"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
